import { facilitiesQuery } from "@common/api/facilities"
import { useCreateToolMutation } from "@common/api/tools"
import { Button } from "@common/components/ui/button"
import { Form } from "@common/components/ui/form"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useSharedForm } from "@common/hooks/use-shared-form"
import { handleError } from "@common/utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import {
  DetailPane,
  DetailPanelActions,
  DetailPanelFields,
} from "@/components/layout/detail-panel"
import { PaneSections } from "@/components/page/pane"
import useCustomToast from "@/hooks/use-custom-toast"
import {
  ToolCalibrationFields,
  ToolIdentityFields,
} from "./-components/tool-fields"
import {
  type ToolFormInput,
  toolCreatePayload,
  toolDefaults,
  toolSchema,
} from "./-lib/tool-form"

export const Route = createFileRoute("/_layout/tools/new")({
  staticData: { detailSidebar: true },
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(facilitiesQuery()),
  component: NewToolPanel,
})

function NewToolPanel(): React.ReactElement {
  const { data: facilities } = useSuspenseQuery(facilitiesQuery())
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const mutation = useCreateToolMutation()
  const canCreate = useHasPermission("tools:create")
  const facility =
    facilities.data.find((candidate) => candidate.is_default) ??
    facilities.data[0]
  const form = useSharedForm<ToolFormInput>({
    resolver: zodResolver(toolSchema),
    defaultValues: toolDefaults(null, facility?.id),
    criteriaMode: "all",
    mode: "onBlur",
  })
  const close = () => navigate({ to: "/tools", search: true })

  const submit = (values: ToolFormInput) =>
    mutation.mutate(
      { toolCreate: toolCreatePayload(values) },
      {
        onSuccess: (created) => {
          showToast("Created", `${created.number} added.`, "success")
          navigate({
            to: "/tools/$toolId",
            params: { toolId: created.id },
            search: true,
          })
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <DetailPane title="New tool" onClose={close}>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(submit)}
          className="flex h-full flex-col"
        >
          <DetailPanelFields>
            <fieldset disabled={!canCreate} className="min-w-0">
              <PaneSections>
                <ToolIdentityFields existing={false} conditionLocked={false} />
                <ToolCalibrationFields />
              </PaneSections>
            </fieldset>
          </DetailPanelFields>
          <DetailPanelActions
            dirty={form.formState.isDirty}
            pending={mutation.isPending}
            onReset={() => form.reset()}
            alwaysShowSave={canCreate}
          >
            <Button type="button" variant="outline" onClick={close}>
              Cancel
            </Button>
          </DetailPanelActions>
        </form>
      </Form>
    </DetailPane>
  )
}
